import { httpsCallable } from 'firebase/functions';
import type { ReviewPointsConfig, CaseStage, CaseTrack } from './bradford';
import { functions } from './firebase';
import type {
  StaffCategory, UserRole, StaffPartyRef,
  SendCase, SendCaseNote, SendDocument, SendAccessLogEntry,
  SendDocumentKind, SendNoteKind, SendPassport,
} from '../types';

/**
 * Reject with a TIMEOUT error if a promise hasn't settled within `ms`. Used for
 * slow MIS/Wonde callables so the UI can show a retry prompt instead of spinning
 * forever (the underlying request keeps running server-side; we just stop waiting).
 */
export function withTimeout<T>(p: Promise<T>, ms = 45000): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), ms),
    ),
  ]);
}

export interface AssessmentRow {
  source: 'insight' | 'assignment';
  parentId: string;
  title: string;
  score: number;
  max: number;
  percent: number | null;
  markedAt: string | null;
}
export const getStudentAssessmentSummary = httpsCallable<{ studentId: string }, { rows: AssessmentRow[] }>(
  functions, 'getStudentAssessmentSummary',
);
export const buildClassFromRosterClass = httpsCallable<{ rosterClassId: string }, { classId: string; created: boolean; students: number }>(
  functions, 'buildClassFromRosterClass',
);

// ─── Reporting (leadership data pack) ─────────────────────────────────────────

export interface SchoolDataPack {
  schoolId: string;
  term: string;
  termStart: string;
  termEnd: string;
  generatedAt: string;
  pupils: number;
  attendance: {
    wholePct: number | null;
    bands: { fine: number; watch: number; pa: number; severe: number; unknown: number };
    byYear: { year: string; pupils: number; pct: number | null }[];
  };
  // behaviour + pastoral are null when the school doesn't hold the behaviour module.
  behaviourModule: boolean;
  behaviour: {
    incidents: number; rewards: number; breaches: number; lates: number; netPoints: number;
    pupilsNegative: number; topCategories: { category: string; count: number }[];
  } | null;
  pastoral: {
    detentions: { total: number; lunch: number; afterschool: number; headteacher: number };
    isolations: { total: number; pupils: number };
    outOfLesson: { total: number; unresolved: number };
  } | null;
}
export const getSchoolDataPack = httpsCallable<{ schoolId?: string }, SchoolDataPack>(functions, 'getSchoolDataPack');

export interface RosterCoverage {
  total: number;
  withFormGroup: number;
  withEmail: number;
  withAttendance: number;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
}
export const getRosterCoverage = httpsCallable<{ schoolId?: string }, RosterCoverage>(functions, 'getRosterCoverage');

/** One row of the student list — only what a list shows (see listStudents). */
export interface StudentListRow {
  id: string;
  name: string;
  forename: string;
  surname: string;
  yearGroup: string | null;
  formGroup: string | null;
  upn: string | null;
  attendancePct: number | null;
}
export const listStudents = httpsCallable<Record<string, never>, { students: StudentListRow[] }>(functions, 'listStudents');

export interface WondeInspectResult {
  wondeSchoolId: string;
  results: { include: string; ok: boolean; status: number; error: string | null; sample: unknown[] }[];
}
export const wondeInspectStudent = httpsCallable<{ schoolId?: string }, WondeInspectResult>(functions, 'wondeInspectStudent');

// ─── Pupil SSO ────────────────────────────────────────────────────────────────

export const linkStaffSsoAccount = httpsCallable<Record<string, never>, { linked: true; role: string; schoolId: string | null }>(
  functions, 'linkStaffSsoAccount',
);

export const linkSsoAccount = httpsCallable<Record<string, never>, { linked: true; role: string; schoolId: string | null }>(
  functions, 'linkSsoAccount',
);

// ─── Voice (survey results) ───────────────────────────────────────────────────

export interface SurveyQuestionResult {
  questionId: string;
  text: string;
  type: 'scale' | 'choice' | 'yesno' | 'text';
  answered: number;
  withheld?: boolean;
  average?: number | null;
  distribution?: Record<string, number>;
  scaleMax?: number;
  counts?: Record<string, number>;
  comments?: string[];
}
export interface SurveyResults {
  surveyId: string;
  title: string;
  anonymous: boolean;
  responseCount: number;
  withheld: boolean;
  minResponses: number;
  results: SurveyQuestionResult[];
  respondents: string[] | null;
}
export const getSurveyResults = httpsCallable<{ surveyId: string }, SurveyResults>(functions, 'getSurveyResults');

// ─── Pastoral (Out Of Lesson, one-way breach) ─────────────────────────────────

export const sendOutOfLesson = httpsCallable<
  { students: { wondeId: string; name: string; yearGroup?: string | null }[]; lesson?: string; room?: string; note?: string },
  { count: number; ids: string[]; emailedStaff: number; emailFailed?: boolean }
>(functions, 'sendOutOfLesson');

/** Pulls behaviour + achievement points from the MIS and rebuilds pupil totals.
 *  `days` defaults to a full academic year — this is the onboarding backfill;
 *  the hourly job only looks back far enough to stay current. */
export interface MisSyncCursor { phase: 'behaviours' | 'achievements'; url: string | null }
export const syncMisBehaviourNow = httpsCallable<
  { schoolId?: string; days?: number; cursor?: MisSyncCursor | null },
  { mirrored: number; pupils: number; done: boolean; cursor: MisSyncCursor | null }
>(functions, 'syncMisBehaviourNow');

export const logOneWayBreach = httpsCallable<
  { studentId: string; studentName: string; door: string },
  { ok: true; alerted: boolean }
>(functions, 'logOneWayBreach');
export const recordMorningLate = httpsCallable<
  { studentId: string; studentName: string; afterNine: boolean },
  { ok: true; minutes: number }
>(functions, 'recordMorningLate');

/** Book-in and sign-out are callables, not client writes: the record says a
 *  child was removed from lessons, so who placed and who released them comes
 *  from the verified caller rather than a field the browser sets. */
export const bookIsolation = httpsCallable<
  { studentId: string; subject?: string; reason: string; expectedMinutes: number },
  { id: string; studentName: string }
>(functions, 'bookIsolation');
export const signOutIsolation = httpsCallable<
  { id: string; signOutReason: 'time-up' | 'leadership' | 'other'; signOutNote?: string },
  { ok: true; alreadyClosed: boolean; actualMinutes?: number }
>(functions, 'signOutIsolation');

// ─── Behaviour Insights (read MIS behaviour via Wonde) ────────────────────────

export interface BehaviourInsights {
  sinceDays: number;
  hasData: boolean;
  summary: {
    behaviourPoints: number; achievementPoints: number; netPoints: number;
    incidents: number; achievements: number; pupilsInvolved: number; parentsNotifiedRate: number;
  };
  previous: { behaviourPoints: number; achievementPoints: number; netPoints: number; incidents: number; achievements: number };
  detentions: { current: number; previous: number; available: boolean };
  exclusions: { count: number; totalDays: number; previous: number; available: boolean; byReason: { reason: string; count: number; days: number }[] };
  /** `pupils` is distinct pupils: 40 incidents from 4 children and 40 from 38
   *  are opposite problems, and a bar draws them identically. */
  byType: {
    type: string; count: number; points: number; pupils: number; prevCount: number;
    topPupils: { studentId: string; count: number; name: string }[];
  }[];
  byLocation: { location: string; count: number; topPupils: { studentId: string; count: number; name: string }[] }[];
  weekly: { week: string; behaviour: number; achievement: number }[];
  byYearGroup: { yearGroup: string; behaviour: number; achievement: number; onRoll: number }[];
  byFormGroup: { formGroup: string; behaviour: number; achievement: number; pupils: number }[];
  byAction: { action: string; count: number }[];
  byDayHour: { day: number; hour: number; count: number }[];
  byDay: { day: number; count: number }[];
  /** False when the MIS records incident dates without a time of day — some
   *  connections stamp every record 00:00:00, and a heatmap that believed them
   *  would draw a fictitious spike at the start of every day. */
  timeOfDayKnown: boolean;
  topPupils: PupilRow[];
  diagnostics: {
    recordsSeen: number;
    recordsInWindow: number;
    filteredOutByCode: number;
    locationMissing: number;
    offRosterRecords: number;
    lastRecordDate: string | null;
  };
}

/** A pupil, as the working list needs them. `prevNet` is what makes it a list
 *  of people to see rather than a leaderboard: who is getting WORSE is not the
 *  same set as who is worst. */
export interface PupilRow {
  studentId: string;
  name: string;
  yearGroup: string;
  formGroup: string | null;
  behaviour: number;
  achievement: number;
  net: number;
  incidents: number;
  achievements: number;
  prevNet: number | null;
  lastIncidentDate: string | null;
  notifiedCount: number;
  attendancePct: number | null;
}
/** What the dashboard actually receives. The aggregate is computed in the
 *  background and read from a snapshot, so the answer carries when it was
 *  computed — and `ready: false` when a school has never had one built. */
export interface BehaviourInsightsResult {
  ready: boolean;
  sinceDays: number;
  /** ISO timestamp of the pass this data came from. */
  computedAt: string | null;
  /** A refresh is running right now — poll rather than starting another. */
  refreshing: boolean;
  /** Why the last refresh failed, if it did. */
  lastError: string | null;
  /** The snapshot is older than the server's staleness threshold. */
  stale: boolean;
  /** How many whole hours old the snapshot is (the threshold, when not ready). */
  staleHours: number;
  data: BehaviourInsights | null;
}
export const getWondeBehaviourInsights = httpsCallable<{ schoolId: string; sinceDays?: number }, BehaviourInsightsResult>(
  functions, 'getWondeBehaviourInsights',
);

/** Rebuild the school's snapshots from the MIS. Minutes at a large school, so
 *  the page fires this and polls getWondeBehaviourInsights — never awaits it. */
export const refreshBehaviourInsights = httpsCallable<
  { schoolId: string },
  { started: boolean; reason?: string; windows?: number[]; records?: number }
>(functions, 'refreshBehaviourInsights');

export interface PupilTimelineEvent {
  date: string; kind: 'behaviour' | 'achievement'; type: string; action: string | null;
  points: number; location: string | null; status: string | null; parentsNotified: boolean;
}
export interface PupilBehaviour {
  studentId: string; name: string; sinceDays: number;
  /** False when this pupil has no stored timeline — they weren't in the last
   *  snapshot's pupils of concern. */
  ready?: boolean;
  computedAt?: string | null;
  /** The stored timeline is capped, so the oldest events may be missing. */
  truncated?: boolean;
  totals: { behaviour: number; achievement: number; net: number; events: number };
  timeline: PupilTimelineEvent[];
}
export const getWondePupilBehaviour = httpsCallable<{ schoolId: string; studentId: string; sinceDays?: number }, PupilBehaviour>(
  functions, 'getWondePupilBehaviour',
);

export const sendOptionChoiceConfirmation = httpsCallable<{ schoolId: string }, { emailed: boolean; reason?: string }>(
  functions, 'sendOptionChoiceConfirmation',
);

export interface PupilLoginRow {
  studentId: string;
  name: string;
  formGroup: string | null;
  yearGroup: string | null;
  username: string;
  /** Only set when (re)generated by this call — shown once, never stored. */
  password: string | null;
  status: 'created' | 'reset' | 'existing' | 'failed';
}
export const provisionPupilLogins = httpsCallable<{ yearGroup: string; regenerate?: boolean }, { rows: PupilLoginRow[] }>(
  functions, 'provisionPupilLogins',
);

export const reportClientError = httpsCallable<
  { message: string; stack?: string; componentStack?: string; path?: string; userAgent?: string },
  { ok: boolean }
>(functions, 'reportClientError');

/** Every step a sign-in can stop at. Mirrors SignInOutcome on the server. */
export type SignInOutcome =
  | 'success' | 'no_match' | 'ambiguous' | 'unverified' | 'school_inactive'
  | 'licence_full' | 'no_email' | 'provider_failed' | 'error';

/**
 * Tell the server about a sign-in it could not otherwise see: a password
 * sign-in, which the Firebase SDK completes in the browser, or a provider popup
 * that refused before any callable was reached. The second is the one that
 * matters — it is where a staff member's "that account doesn't exist" lives.
 *
 * Best-effort by nature. Never block a sign-in on it, and never await it in a
 * path the user is waiting on.
 */
export const reportSignIn = httpsCallable<
  { outcome: SignInOutcome; provider: string; email?: string; detail?: string; userAgent?: string },
  { ok: boolean }
>(functions, 'reportSignIn');

/** Fire-and-forget: a telemetry call must never be why somebody cannot sign in. */
export function noteSignIn(outcome: SignInOutcome, provider: string, email?: string, detail?: string): void {
  void reportSignIn({
    outcome, provider,
    ...(email ? { email } : {}),
    ...(detail ? { detail: detail.slice(0, 300) } : {}),
    userAgent: navigator.userAgent,
  }).catch(() => { /* telemetry is never load-bearing */ });
}

export interface SignInEventRow {
  id: string; at: string; outcome: SignInOutcome; provider: string;
  email: string | null; schoolId: string | null; role: string | null;
  reportedBy: 'server' | 'client'; detail: string | null;
}
export const getSignInEvents = httpsCallable<
  { sinceDays?: number; limit?: number; schoolId?: string; outcome?: string },
  {
    sinceDays: number;
    events: SignInEventRow[];
    summary: { attempts: number; people: number; signedIn: number; blocked: number; byOutcome: Record<string, number> };
    truncated: boolean;
  }
>(functions, 'getSignInEvents');

export type FeedbackKind = 'feature' | 'problem' | 'other';
export const submitFeedback = httpsCallable<
  { kind: FeedbackKind; message: string; path: string; userAgent: string },
  { ok: boolean; id: string }
>(functions, 'submitFeedback');
export interface FeedbackRow {
  id: string; createdAt: string; kind: FeedbackKind; message: string; path: string;
  email: string | null; displayName: string | null; role: string | null;
  schoolId: string | null; schoolName: string | null; status: 'new' | 'seen' | 'done' | string;
}
export const getFeedback = httpsCallable<
  { limit?: number; schoolId?: string },
  { items: FeedbackRow[]; truncated: boolean }
>(functions, 'getFeedback');
export const setFeedbackStatus = httpsCallable<
  { id: string; status: 'new' | 'seen' | 'done' },
  { ok: boolean }
>(functions, 'setFeedbackStatus');

export interface BehaviourCodes {
  sinceDays: number;
  /** False until the school's first snapshot has been built. */
  ready?: boolean;
  computedAt?: string | null;
  behaviourTypes: { code: string; count: number }[];
  achievementTypes: { code: string; count: number }[];
}
export const getWondeBehaviourCodes = httpsCallable<{ schoolId: string; sinceDays?: number }, BehaviourCodes>(
  functions, 'getWondeBehaviourCodes',
);

// ─── provisionSchool ──────────────────────────────────────────────────────────

export interface ProvisionSchoolInput {
  schoolName: string;
  adminEmail: string;
  adminDisplayName: string;
  urn?: string;
  localAuthority?: string;
  plan?: 'trial' | 'paid';
  trialEndsAt?: string;   // ISO date string — only used when plan = 'trial'
  licenceCount?: number;  // 0 = unlimited
  modules?: string[];     // module ids to activate; omitted = all
}
export interface ProvisionSchoolResult {
  schoolId: string;
  adminUid: string;
  setupLink?: string | null;
  emailConfigured?: boolean;
}
export const provisionSchool = httpsCallable<ProvisionSchoolInput, ProvisionSchoolResult>(
  functions, 'provisionSchool',
);

// ─── createUser ───────────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string;
  displayName: string;
  role: UserRole;
  staffCategory?: StaffCategory;
  lineManagerId?: string;
  schoolId?: string;
}
export interface CreateUserResult {
  uid: string;
}
export const createUser = httpsCallable<CreateUserInput, CreateUserResult>(
  functions, 'createUser',
);

// ─── deactivateUser ───────────────────────────────────────────────────────────

export interface DeactivateUserInput {
  targetUid: string;
}
export const deactivateUser = httpsCallable<DeactivateUserInput, { success: boolean }>(
  functions, 'deactivateUser',
);
export const reactivateUser = httpsCallable<{ targetUid: string }, { success: boolean }>(
  functions, 'reactivateUser',
);

// ─── listSchools ──────────────────────────────────────────────────────────────

export interface SchoolSummary {
  id: string;
  name: string;
  isActive: boolean;
  urn?: string | null;
  localAuthority?: string | null;
  plan?: string | null;           // 'trial' | 'paid' | 'suspended'
  trialEndsAt?: string | null;    // ISO date string
  contractEndsAt?: string | null; // ISO date string (paid plan)
  adminNotes?: string | null;
  userCount: number;
  licenceCount: number;
  activeStaffCount: number;
  legalAcceptedAt?: string | null;
  legalAcceptedByName?: string | null;
  createdAt?: string | null;
  /** moduleId → entitlement — which platform modules the school holds */
  modules?: Record<string, { active?: boolean; expiresAt?: string | null }>;
  wondeSchoolId?: string | null;
  wondeSyncEnabled?: boolean;
  hasWondeToken?: boolean;
  wondeLastSyncAt?: string | null;
  wondeLastSyncStatus?: string | null;
  wondeLastSyncError?: string | null;
}
export const listSchools = httpsCallable<Record<string, never>, SchoolSummary[]>(
  functions, 'listSchools',
);
export const getSchool = httpsCallable<{ schoolId: string }, SchoolSummary>(
  functions, 'getSchool',
);

// ─── launchCycle ─────────────────────────────────────────────────────────────

export const launchCycle = httpsCallable<{ cycleId: string }, { reviewsCreated: number }>(
  functions, 'launchCycle',
);

// ─── triggerMidYear ───────────────────────────────────────────────────────────

// Pass `reviewIds` to open mid-year for just those reviews (the per-row action
// on the cycle overview); leave it out to open every confirmed review. Either
// way the server refuses before the cycle's mid-year date.
export const triggerMidYear = httpsCallable<
  { cycleId: string; reviewIds?: string[] },
  { opened: number; alreadyOpen?: boolean; skipped?: number }
>(
  functions, 'triggerMidYear',
);

/**
 * The message to show when a callable fails. A deliberate refusal from the
 * server is already a sentence ("Mid-year opens on 1 February 2027."), so it is
 * shown as it is; anything else (a dropped connection, an internal error)
 * arrives as a bare code, so the caller's plain-English fallback is used.
 */
export function callableMessage(e: unknown, fallback: string): string {
  const { code, message } = (e ?? {}) as { code?: string; message?: string };
  const worded = ['functions/failed-precondition', 'functions/invalid-argument', 'functions/not-found', 'functions/permission-denied'];
  return code && worded.includes(code) && message ? message : fallback;
}

// ─── reopenReview ─────────────────────────────────────────────────────────────

// schoolAdmin only: sends a confirmed review back to its line manager to amend
// and confirm again. The reason is shown to both people and audited.
export const reopenReview = httpsCallable<{ reviewId: string; reason: string }, { ok: true }>(
  functions, 'reopenReview',
);
// The server allows 300 s — a closed cycle's files, reviews and audit trail
// all go — so the client waits that long rather than giving up at 70 s.
export const deleteCycle = httpsCallable<
  { cycleId: string },
  { success: boolean; reviewsDeleted?: number; filesRemoved?: number }
>(functions, 'deleteCycle', { timeout: 300_000 });

// ─── getSchoolUsers ───────────────────────────────────────────────────────────

export interface UserSummary {
  id: string;
  displayName: string;
  email: string;
  role: UserRole;
  staffCategory?: StaffCategory;
  lineManagerId?: string;
  isFormTutor?: boolean;
  isPastoralLeader?: boolean;
  isAcademicLeader?: boolean;
  isSLT?: boolean;
  isAtTopOfPaySpine?: boolean;
  isActive: boolean;
  lastSignIn?: string | null;
  createdAt?: string | null;
}
export const getSchoolUsers = httpsCallable<{ schoolId: string }, UserSummary[]>(
  functions, 'getSchoolUsers',
);

export const renameSchool = httpsCallable<{ schoolId: string; name: string }, { success: boolean }>(
  functions, 'renameSchool',
);

export const deactivateSchool = httpsCallable<{ schoolId: string; isActive: boolean }, { success: boolean }>(
  functions, 'deactivateSchool',
);

// ─── getSchoolStats ───────────────────────────────────────────────────────────

export interface SchoolStats {
  totalCycles: number;
  activeCycleName: string | null;
  totalReviews: number;
}
export const getSchoolStats = httpsCallable<{ schoolId: string }, SchoolStats>(
  functions, 'getSchoolStats',
);

// ─── updateUserAdmin ──────────────────────────────────────────────────────────

export const updateUserAdmin = httpsCallable<
  { targetUid: string; role?: string; staffCategory?: string; lineManagerId?: string },
  { success: boolean }
>(functions, 'updateUserAdmin');

// ─── getSchoolCycles ──────────────────────────────────────────────────────────

export interface CycleSummary {
  id: string;
  name: string;
  status: string;
  staffCategory: string;
  startDate: string | null;
  staffDeadline: string | null;
  lineManagerDeadline: string | null;
  reviewCount: number;
  createdAt: string | null;
}
export const getSchoolCycles = httpsCallable<{ schoolId: string }, CycleSummary[]>(
  functions, 'getSchoolCycles',
);

// ─── getCycleReviewStats ──────────────────────────────────────────────────────

export interface CycleReviewStats {
  cycleName: string;
  status: string;
  staffCategory: string;
  startDate: string | null;
  staffDeadline: string | null;
  lineManagerDeadline: string | null;
  totalReviews: number;
  byStatus: Record<string, number>;
  reviews: { id: string; revieweeName: string; reviewerName: string; status: string }[];
}
export const getCycleReviewStats = httpsCallable<
  { cycleId: string; schoolId: string },
  CycleReviewStats
>(functions, 'getCycleReviewStats');

// ─── bulkCreateUsers ──────────────────────────────────────────────────────────

export interface BulkCreateResult {
  created: number;
  failed: { email: string; error: string }[];
}
export const bulkCreateUsers = httpsCallable<
  { schoolId: string; users: { email: string; displayName: string; role: string; staffCategory?: string }[] },
  BulkCreateResult
>(functions, 'bulkCreateUsers');

// ─── setSchoolPlan ────────────────────────────────────────────────────────────

export interface SetSchoolPlanInput {
  schoolId: string;
  plan?: 'trial' | 'paid' | 'suspended';
  trialEndsAt?: string | null;
  contractEndsAt?: string | null;
  adminNotes?: string | null;
}
export const setSchoolPlan = httpsCallable<SetSchoolPlanInput, { success: boolean }>(
  functions, 'setSchoolPlan',
);

// ─── setSchoolModules ─────────────────────────────────────────────────────────

export interface SetSchoolModulesInput {
  schoolId: string;
  modules: Record<string, { active: boolean; expiresAt?: string | null }>;
}
export const setSchoolModules = httpsCallable<SetSchoolModulesInput, { success: boolean }>(
  functions, 'setSchoolModules',
);

// ─── Leave & Cover module ─────────────────────────────────────────────────────

export interface SubmitLeaveRequestInput {
  /** YYYY-MM-DD — one day per request */
  date: string;
  typeId: string;
  reason: string;
  /** Period ids they are away for */
  absencePeriodIds: string[];
  /** Period ids needing cover — must be a subset of absencePeriodIds */
  coverPeriodIds: string[];
  /** The only text the cover team sees */
  coverNotes: string;
  /** Must be true; the server refuses without it */
  discussedWithLineManager: boolean;
  /** Storage path of an already-uploaded attachment, inside the caller's prefix */
  evidencePath?: string;
  evidenceName?: string;
}
export const submitLeaveRequest = httpsCallable<
  SubmitLeaveRequestInput,
  { requestId: string; routedTo: 'lineManager' | 'slt'; urgent: boolean; approverName: string }
>(functions, 'submitLeaveRequest');

/** Both approval stages — which one applies is inferred from the request's own
 *  status, so the caller cannot decide at the wrong stage. */
export const decideLeaveRequest = httpsCallable<
  { requestId: string; decision: 'approved' | 'declined'; comment?: string },
  { success: boolean; nextStatus: string }
>(functions, 'decideLeaveRequest');

export const withdrawLeaveRequest = httpsCallable<{ requestId: string }, { success: boolean }>(
  functions, 'withdrawLeaveRequest',
);

/**
 * Evidence is never readable straight from Storage — this checks the approval
 * chain and audits the access. Below 6 MB it returns the BYTES, which the
 * client turns into a tab-local blob: URL; only above that is a link issued,
 * and that link is shareable and cannot be revoked, so the UI must say so.
 * Same contract as openSendDocument — see openProtectedFile().
 */
export type LeaveEvidenceResult =
  | { mode: 'inline'; name: string; contentType: string; dataBase64: string }
  | { mode: 'link'; name: string; contentType: string; url: string; shareable: true; sizeBytes: number };

export const getLeaveEvidenceUrl = httpsCallable<{ requestId: string }, LeaveEvidenceResult>(
  functions, 'getLeaveEvidenceUrl',
);

/** Super admin only — runs the escalation sweep for one school on demand. */
export const runLeaveEscalationNow = httpsCallable<
  { schoolId: string },
  { escalated: number }
>(functions, 'runLeaveEscalationNow');

// ─── Assessment Insights module ───────────────────────────────────────────────

export interface InsightsClassRollup {
  classId: string;
  className: string;
  teacherName: string;
  subject: string | null;
  isFullyMarked: boolean;
  studentCount: number;
  completedStudentCount: number;
  overallPercent: number | null;
  overallBand: string;
  toleranceBreaches: number;
}

export interface InsightsAssessmentRollup {
  id: string;
  name: string;
  academicYear: string | null;
  ownerName: string;
  classCount: number;
  fullyMarkedClassCount: number;
  studentCount: number;
  completedStudentCount: number;
  overallPercent: number | null;
  overallBand: string;
  toleranceBreaches: number;
  questionCount: number;
  classes: InsightsClassRollup[];
}

export interface InsightsOverviewResponse {
  schoolAveragePercent: number | null;
  assessmentCount: number;
  fullyMarkedCount: number;
  bandCounts: { weak: number; mixed: number; strong: number; ungraded: number };
  subjects: { subject: string; classCount: number; averagePercent: number | null }[];
  assessments: InsightsAssessmentRollup[];
}

export const getAssessmentInsightsOverview = httpsCallable<
  { schoolId?: string },
  InsightsOverviewResponse
>(functions, 'getAssessmentInsightsOverview');

export interface InterventionFlag {
  insightId: string; insightName: string; academicYear: string | null; subject: string | null;
  classId: string; className: string; teacherName: string;
  questionId: string; questionLabel: string; questionPrompt: string | null;
  classPercent: number; cohortPercent: number; gap: number; scoredStudents: number;
}
export const getInterventionFlags = httpsCallable<{ schoolId?: string }, { assessmentsScanned: number; flags: InterventionFlag[] }>(
  functions, 'getInterventionFlags',
);

// ─── Trust mode ───────────────────────────────────────────────────────────────

export const createTrust = httpsCallable<{ name: string }, { trustId: string }>(
  functions, 'createTrust',
);

export interface TrustSummary {
  id: string;
  name: string;
  schools: { id: string; name: string }[];
}
export const listTrusts = httpsCallable<Record<string, never>, TrustSummary[]>(
  functions, 'listTrusts',
);

export const setSchoolTrust = httpsCallable<
  { schoolId: string; trustId: string | null },
  { success: boolean }
>(functions, 'setSchoolTrust');

export const createTrustAdmin = httpsCallable<
  { trustId: string; email: string; displayName: string },
  { uid: string; setupLink: string | null; emailConfigured: boolean }
>(functions, 'createTrustAdmin');

export interface TrustSchoolOverview {
  id: string;
  name: string;
  isActive: boolean;
  plan: string;
  staffCount: number;
  pendingLeave: number;
  activeModules: string[];
}
export const getTrustOverview = httpsCallable<
  { trustId?: string },
  { trustName: string; schoolCount: number; totalStaff: number; schools: TrustSchoolOverview[] }
>(functions, 'getTrustOverview');

// ─── Wellbeing module ─────────────────────────────────────────────────────────

export const submitWellbeingCheckin = httpsCallable<
  { score: number; comment?: string },
  { success: boolean; weekKey: string }
>(functions, 'submitWellbeingCheckin');

export interface WellbeingWeek {
  weekKey: string;
  responses: number;
  averageScore: number | null;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number> | null;
  comments: string[];
  belowThreshold: boolean;
}

export const getWellbeingDashboard = httpsCallable<
  { schoolId?: string },
  { weeks: WellbeingWeek[]; staffCount: number; minResponses: number }
>(functions, 'getWellbeingDashboard');

export interface PupilWellbeingSummary {
  studentId: string;
  studentName: string;
  latestScore: number;
  latestAt: string;
  averageScore: number;
  count: number;
  recent: { date: string; score: number }[];
  low: boolean;
}

export interface StudentWellbeingDashboard {
  overview: {
    weeks: { weekKey: string; responses: number; averageScore: number | null }[];
    latestAverage: number | null;
    distribution: Record<'1' | '2' | '3' | '4' | '5', number> | null;
    pupilsCheckedIn: number;
  };
  students: PupilWellbeingSummary[];
  windowWeeks: number;
}

export const getStudentWellbeingDashboard = httpsCallable<
  { schoolId?: string },
  StudentWellbeingDashboard
>(functions, 'getStudentWellbeingDashboard');

// ─── Timetable (MIS via Wonde) ────────────────────────────────────────────────

export interface TimetableLesson {
  start: string | null;
  end: string | null;
  day: string | null;
  periodName: string | null;
  className: string;
  /** Optional: absent from timetable entries cached before this was added. */
  classId?: string;
  room: string | null;
}
export interface MyTimetable {
  connected: boolean;
  /** Why there is no timetable, when connected is false. */
  reason?: string;
  weekStart: string;
  weekOffset: number;
  lessons: TimetableLesson[];
}
export const getMyTimetable = httpsCallable<{ weekOffset?: number }, MyTimetable>(
  functions, 'getMyTimetable',
);

// ─── School calendar (iCal subscription, platform feature) ────────────────────

export interface CalendarEvent {
  uid?: string;
  summary: string;
  start: string;       // ISO-8601
  end: string | null;  // ISO-8601, or null
  allDay: boolean;
  location?: string;
}
export const getSchoolCalendar = httpsCallable<
  Record<string, never>,
  { events: CalendarEvent[]; configured: boolean }
>(functions, 'getSchoolCalendar');

// ─── Staff ↔ MIS (Wonde staff linking / provisioning) ─────────────────────────

export interface StaffMisRow { userId: string; name: string; email: string; role: string; wondeStaffId: string | null; misName: string | null; }
export interface MisStaff { wondeId: string; name: string; email: string | null; }
export const getStaffMisStatus = httpsCallable<Record<string, never>, { staff: StaffMisRow[]; unmatchedMis: MisStaff[] }>(functions, 'getStaffMisStatus');
export const linkStaffToMis = httpsCallable<{ userId: string; wondeStaffId: string }, { success: true }>(functions, 'linkStaffToMis');
export const unlinkStaffFromMis = httpsCallable<{ userId: string }, { success: true }>(functions, 'unlinkStaffFromMis');
export const provisionMisStaff = httpsCallable<{ wondeStaffId?: string; all?: boolean }, { created: number }>(functions, 'provisionMisStaff');
/** Set who will line-manage someone before either has an account; `manager: null` clears it.
 *  Applies straight away when both already have accounts, so the roster can use it for every mix. */
export const setPendingLineManager = httpsCallable<
  { subject: StaffPartyRef; manager: StaffPartyRef | null },
  { pending: boolean }
>(functions, 'setPendingLineManager');

export interface WellbeingBenchmark {
  scope: 'trust' | 'platform';
  schoolAverage: number | null;
  schoolResponses: number;
  peerAverage: number | null;
  peerSchoolCount: number;
  minPeerSchools: number;
  weeks: number;
}
export const getWellbeingBenchmark = httpsCallable<{ schoolId?: string }, WellbeingBenchmark>(
  functions, 'getWellbeingBenchmark',
);

// ─── Wonde sync ───────────────────────────────────────────────────────────────

export const setWondeConfig = httpsCallable<
  { schoolId: string; wondeSchoolId?: string | null; enabled?: boolean; token?: string | null },
  { success: boolean }
>(functions, 'setWondeConfig');

/** The school admin's "Assign a line manager" To Do item, counted on the
 *  server so Home never downloads the whole staff directory. */
export const getLineManagerGaps = httpsCallable<
  Record<string, never> | undefined,
  { signedIn: number; notSignedIn: number; names: string[]; hasLiveCycle: boolean }
>(functions, 'getLineManagerGaps');

export const syncWondeNow = httpsCallable<
  { schoolId: string },
  { students: number; staff: number }
>(functions, 'syncWondeNow');

// ─── setLicenceCount ──────────────────────────────────────────────────────────

export const setLicenceCount = httpsCallable<
  { schoolId: string; licenceCount: number },
  { success: boolean }
>(functions, 'setLicenceCount');

// ─── acceptLegalTerms ─────────────────────────────────────────────────────────

export const acceptLegalTerms = httpsCallable<
  { schoolId: string; version: string },
  { success: boolean }
>(functions, 'acceptLegalTerms');

// ─── enrollStaffInCycle ───────────────────────────────────────────────────────

export interface EnrollStaffInput {
  staffUid: string;
  cycleId: string;
  roleFlagsOverride?: {
    isFormTutor: boolean;
    isPastoralLeader: boolean;
    isAcademicLeader: boolean;
    isSLT: boolean;
    isAtTopOfPaySpine: boolean;
  };
}
export const enrollStaffInCycle = httpsCallable<EnrollStaffInput, { reviewId: string }>(
  functions, 'enrollStaffInCycle',
);

// ─── purgeCycleData ───────────────────────────────────────────────────────────

// The server allows 300 s (a large cycle has thousands of files and audit
// entries to clear), so the client waits that long too rather than giving up
// at the default 70 s while the purge carries on.
export const purgeCycleData = httpsCallable<
  { cycleId: string },
  { success: boolean; reviewsPurged: number; filesRemoved?: number }
>(functions, 'purgeCycleData', { timeout: 300_000 });

// ─── SEND & EHCP module ───────────────────────────────────────────────────────
//
// Note what is NOT here: there is no wrapper for reading a case. Tier 2 lives
// behind callables that audit every access, and those arrive with Phase 2.

// The caller's SEND tier is NOT a callable. It is derived live from sendConfig
// in SchoolContext, the same way isMorningLateOfficer is — one subscription the
// app already holds, no round trip, and it updates the moment the SENCo changes
// the team. It is cosmetic: every actual grant is re-checked server-side on the
// request that uses it, and in firestore.rules on every direct read.

export interface SetSendTeamInput {
  sencoUids: string[];
  sendTeamUids: string[];
  leadershipViewerUids: string[];
}
export const setSendTeam = httpsCallable<SetSendTeamInput, {
  ok: boolean;
  sencoUids: string[];
  sendTeamUids: string[];
  leadershipViewerUids: string[];
  memberNames: Record<string, string>;
}>(functions, 'setSendTeam');

export interface UpsertSendRegisterEntryInput {
  studentId: string;
  status: 'ehcp' | 'senSupport' | 'monitoring' | 'ceased';
  /** yyyy-mm-dd, or null to clear */
  planStartDate?: string | null;
  lastReviewDate?: string | null;
  adminNote?: string;
}
export const upsertSendRegisterEntry = httpsCallable<UpsertSendRegisterEntryInput, {
  ok: boolean; id: string; nextReviewDue: string | null; overdue: boolean;
}>(functions, 'upsertSendRegisterEntry');

export const setSendCaseworkers = httpsCallable<
  { studentId: string; caseworkerUids: string[] },
  { ok: boolean; caseworkerUids: string[] }
>(functions, 'setSendCaseworkers');

export const archiveSendRegisterEntry = httpsCallable<
  { studentId: string; reason?: string },
  { ok: boolean }
>(functions, 'archiveSendRegisterEntry');

// ─── SEND — Tier 2, the case ──────────────────────────────────────────────────
//
// All callables, because sendCases/sendCaseNotes/sendDocuments/sendAccessLog are
// closed to clients in the rules. There is no listener on a case: saveSendCase
// carries the version it was read at and is refused if the record moved
// underneath, which is what replaces the live update.

export interface GetSendCaseResult {
  exists: boolean;
  studentId: string;
  studentName: string;
  yearGroup: string | null;
  /** Pass back unchanged when saving. 0 means the case has never been written. */
  version: number;
  case: SendCase | null;
}
export const getSendCase = httpsCallable<{ studentId: string }, GetSendCaseResult>(
  functions, 'getSendCase',
);

/** Everything on the case form, plus the version it was loaded at. */
export type SaveSendCaseInput = Partial<Omit<SendCase,
  'schoolId' | 'studentName' | 'retainUntil' | 'version' | 'createdAt' | 'updatedAt' | 'updatedByUid' | 'updatedByName'
>> & { studentId: string; version: number };

export const saveSendCase = httpsCallable<SaveSendCaseInput, {
  ok: boolean; version: number; retainUntil: string | null;
}>(functions, 'saveSendCase');

export const getSendCaseNotes = httpsCallable<{ studentId: string }, { notes: SendCaseNote[] }>(
  functions, 'getSendCaseNotes',
);

export const addSendCaseNote = httpsCallable<{
  studentId: string; body: string; kind: SendNoteKind; occurredAt?: string | null;
}, { ok: boolean; id: string }>(functions, 'addSendCaseNote');

/** Upload to Storage first, then register the path here — the callable
 *  re-derives the prefix from the verified caller and refuses anything else. */
export const attachSendDocument = httpsCallable<{
  studentId: string; storagePath: string; fileName: string;
  kind: SendDocumentKind; documentDate?: string | null;
}, { ok: boolean; id: string }>(functions, 'attachSendDocument');

/** Never carries storagePath — the client has no legitimate use for it. */
export type SendDocumentSummary = Omit<SendDocument, 'storagePath' | 'uploadedByUid'>;

export const listSendDocuments = httpsCallable<{ studentId: string }, { documents: SendDocumentSummary[] }>(
  functions, 'listSendDocuments',
);

/**
 * Fetch one document. Authorised and audited on every single call — there is no
 * cacheable link to hold on to.
 *
 * 'inline' (anything up to 6 MB, which is nearly everything) returns the bytes
 * themselves, so no shareable URL is ever created. 'link' is the fallback for
 * files too large to return through a callable: that URL IS shareable and
 * cannot be revoked — Firebase download tokens append rather than replace — so
 * the UI must say so rather than handing it over quietly.
 */
export type OpenSendDocumentResult =
  | { mode: 'inline'; name: string; contentType: string; dataBase64: string }
  | { mode: 'link'; name: string; contentType: string; url: string; shareable: true; sizeBytes: number };

export const openSendDocument = httpsCallable<{ documentId: string }, OpenSendDocumentResult>(
  functions, 'openSendDocument',
);

export const getSendAccessLog = httpsCallable<{ studentId: string }, { entries: SendAccessLogEntry[] }>(
  functions, 'getSendAccessLog',
);

/** The subject access request extract: everything held about one pupil. */
export const exportSendCase = httpsCallable<{ studentId: string }, {
  exportedAt: unknown;
  exportedByName: string;
  register: Record<string, unknown>;
  case: SendCase | null;
  notes: SendCaseNote[];
  documents: SendDocumentSummary[];
  accessLog: SendAccessLogEntry[];
}>(functions, 'exportSendCase');

// ─── SEND — Tier 0, the passport ──────────────────────────────────────────────

/** Exactly the eight classroom fields. The callable REFUSES anything else. */
export interface PublishSendPassportInput {
  studentId: string;
  passport: {
    summary: string;
    supportProfile: string[];
    strategies: string[];
    accessArrangements: string[];
    strengths: string;
    classroomBarriers: string;
    communicationPreferences: string;
    namedStaffUids: string[];
  };
}
export const publishSendPassport = httpsCallable<PublishSendPassportInput, {
  ok: boolean; audience: number; unlinkedTeachers: number; truncated: boolean; maxAudience: number;
}>(functions, 'publishSendPassport');

/**
 * One pupil's passport, with the audience recomputed live first — so a teacher
 * who picked up the class this morning is not blocked until the sync runs.
 * "No passport" and "not yours" are deliberately the same answer.
 */
export const getPupilPassport = httpsCallable<{ studentId: string },
  { exists: false } | { exists: true; passport: SendPassport }
>(functions, 'getPupilPassport');

export const recomputePassportAudience = httpsCallable<Record<string, never>, {
  ok: boolean; passports: number; changed: number;
}>(functions, 'recomputePassportAudience');

// ─── SEND — Section F provision ───────────────────────────────────────────────

export interface UpsertSendProvisionInput {
  provisionId?: string;
  studentId: string;
  sectionFRef?: string;
  description: string;
  provisionType?: string;
  frequency: string;
  sessionsPerCycle?: number;
  minutesPerSession?: number;
  /** An account to log delivery, or blank plus a role label for "the TA team". */
  deliveredByUid?: string;
  deliveredByRoleLabel?: string;
  startDate?: string;
  endDate?: string | null;
  status?: string;
  annualCost?: number | null;
}
export const upsertSendProvision = httpsCallable<UpsertSendProvisionInput, { ok: boolean; id: string; nextDueDate: string | null }>(
  functions, 'upsertSendProvision',
);
export const endSendProvision = httpsCallable<{ provisionId: string; reason?: string }, { ok: boolean }>(
  functions, 'endSendProvision',
);

export interface ProvisionComplianceRow {
  id: string; studentId: string; studentName: string;
  sectionFRef: string; description: string; frequency: string; status: string;
  deliveredByName: string | null; nextDueDate: string | null; lastDeliveredOn: string | null;
  expected: number; delivered: number; percent: number | null; overdue: boolean;
}
export interface ProvisionComplianceResult {
  from: string; to: string;
  overallPercent: number | null; expectedTotal: number; deliveredTotal: number; overdueCount: number;
  provisions: ProvisionComplianceRow[];
  pupils: { studentId: string; studentName: string; provisions: number; overdue: number; percent: number | null }[];
}
/** Server-side rollup — at 1,000 pupils the logs run to ~200k a year. */
export const getProvisionCompliance = httpsCallable<{ days?: number; to?: string; studentId?: string }, ProvisionComplianceResult>(
  functions, 'getProvisionCompliance',
);

// ─── Staff Absence module ─────────────────────────────────────────────────────
//
// Everything is a callable: `staffAbsences` is closed to clients, so the viewer
// list in staffAbsenceConfig is checked on the server on every request. Whether
// to SHOW the pages comes from `useSchool().isStaffAbsenceViewer`, which is
// cosmetic in the same way sendTier is.

export type StaffTypeFilter = 'all' | 'teaching' | 'support';

/** One absence type the MIS uses, as offered on a report page's tick-list. */
export interface AbsenceTypeInfo {
  type: string;
  count: number;
  annualLeave: boolean;
  /** Whether the school's Setup list counts it — the tick-list's resting state. */
  countsByDefault: boolean;
}

export const setStaffAbsenceViewers = httpsCallable<{ viewerUids: string[] }, {
  ok: boolean; viewerUids: string[]; memberNames: Record<string, string>;
}>(functions, 'setStaffAbsenceViewers');

export interface StaffAbsenceSyncState {
  /** ISO timestamp of the last sync, or null if it has never run. */
  syncedAt: string | null;
  syncStatus: 'ok' | 'not-shared' | 'error' | 'never' | string;
  syncError: string | null;
}

export const getStaffAbsenceTypes = httpsCallable<Record<string, never>, StaffAbsenceSyncState & {
  types: { type: string; count: number; annualLeave: boolean; description: string | null }[];
  /** Who would be where over the last twelve months with the current points — counts only. */
  preview: { from: string; to: string; thresholds: number[]; labels: string[]; bandCounts: number[] };
}>(functions, 'getStaffAbsenceTypes');

export interface BradfordRow {
  employeeId: string;
  name: string;
  staffCode: string | null;
  initials: string | null;
  teaching: boolean | null;
  spells: number;
  daysLost: number;
  hours: number | null;
  bradford: number;
  /** 0 = under the first trigger point; n = at or over the nth. */
  band: number;
  avgSpellDays: number;
  longestSpellDays: number;
  monFriStartPct: number | null;
  lastAbsentDay: string | null;
  patterns: AbsencePattern[];
}

export interface AbsencePattern {
  kind: 'weekday' | 'weekendAdjacent' | 'holiday' | 'annualLeave' | 'sameWeek' | 'recent' | 'selfCert';
  label: string;
  detail: string;
  count: number;
  of: number;
}

export interface BradfordTotals {
  staff: number;
  spells: number;
  daysLost: number;
  overTop: number;
  bandCounts: number[];
  headcount: number;
  daysPerHead: number | null;
  /** People with at least one pattern chip. */
  withPattern: number;
}

export interface BradfordReport extends StaffAbsenceSyncState {
  from: string;
  to: string;
  /** True if `from` was pulled forward to the start of the stored window. */
  clamped: boolean;
  windowStart: string;
  today: string;
  staffTypeAvailable: boolean;
  thresholds: number[];
  thresholdLabels: string[];
  includedAbsenceTypes: string[] | null;
  absenceTypes: AbsenceTypeInfo[];
  /** The tick-list this report was run with, or null for the school default. */
  absenceTypeOverride: string[] | null;
  totals: BradfordTotals;
  /** The same-length window before this one, or null if history does not cover it. */
  previous: { from: string; to: string; staff: number; spells: number; daysLost: number; overTop: number } | null;
  newlyCrossed: { employeeId: string; name: string; staffCode: string | null; bandThen: number; bandNow: number; bradford: number }[];
  lookbackDays: number;
  byMonth: { month: string; label: string; spells: number; days: number }[];
  byType: { type: string; spells: number; days: number; counts: boolean; annualLeave: boolean }[];
  longTerm: { employeeId: string; name: string; staffCode: string | null; startDate: string; lastDay: string; days: number; absenceType: string; ongoing: boolean }[];
  longTermMinDays: number;
  dayOfWeek: { starts: number[]; monFriPct: number | null };
  /** Whether term dates are configured — the holiday pattern needs them. */
  termsConfigured: boolean;
  /** How many people show each pattern kind. */
  patternCounts: Record<string, number>;
  rows: BradfordRow[];
}

export const getBradfordReport = httpsCallable<
  { from: string; to: string; staffType?: StaffTypeFilter; absenceTypes?: string[] },
  BradfordReport
>(functions, 'getBradfordReport');

export interface StaffMemberSpell {
  startDate: string;
  lastDay: string;
  days: number;
  totalDays: number;
  hours: number | null;
  absenceType: string;
  description: string | null;
  records: number;
  counts: boolean;
  longTerm: boolean;
  monFri: boolean;
}

export interface StaffMemberStats {
  spells: number;
  days: number;
  hours: number | null;
  avgSpellDays: number;
  longestSpellDays: number;
  monFriStartPct: number | null;
  firstAbsentDay: string | null;
  lastAbsentDay: string | null;
  bradford: number;
  band: number;
}

export interface StaffMemberAbsence extends StaffAbsenceSyncState {
  from: string;
  to: string;
  clamped: boolean;
  windowStart: string;
  today: string;
  person: { employeeId: string; name: string; staffCode: string | null; teaching: boolean | null; current: boolean | null };
  thresholds: number[];
  thresholdLabels: string[];
  includedAbsenceTypes: string[] | null;
  absenceTypes: AbsenceTypeInfo[];
  absenceTypeOverride: string[] | null;
  stats: StaffMemberStats;
  previous: (StaffMemberStats & { from: string; to: string }) | null;
  rolling: { date: string; score: number; spells: number; days: number; band: number }[];
  /** The review-point measure as at the end of the range. */
  reviewConfig: ReviewPointsConfig;
  reviewPoints: PersonReviewPoints;
  /** The third review point (9.3(c)), decided on the server so this page and
   *  the list never disagree about what counts as one. */
  patternPoint: boolean;
  /** The same detectors over the rolling year, which is the window the review
   *  point uses. Shown when the picked range finds nothing. */
  patternsOverYear: AbsencePattern[];
  patternYearFrom: string;
  /** Term bands as staff experience them, for shading the timeline's holidays. */
  staffTerms: { name: string; start: string; end: string }[];
  /** Occasions and days in the rolling window at each month end, plus today. */
  rollingReview: { date: string; occasions: number; days: number; reached: boolean }[];
  /** Their case in the procedure (open or closed), or null. */
  case: { case: StaffAbsenceCase; evaluation: StageEvaluation } | null;
  /** Their returns in the range, with the discussion record if ticked. */
  returns: ReturnRow[];
  byType: { type: string; spells: number; days: number; counts: boolean; annualLeave: boolean }[];
  patterns: AbsencePattern[];
  termsConfigured: boolean;
  /** Monday–Friday: how many counted spells began on each day, and how many
   *  working days were lost on each. */
  weekday: { starts: number[]; ends: number[]; days: number[] };
  /** Working days lost in each month of the range, and the occasions that
   *  began in it (one already under way is booked to the opening month). */
  byMonth: { month: string; label: string; spells: number; days: number }[];
  spells: StaffMemberSpell[];
}

/** Audited on the server on every call — see the module note in CLAUDE.md. */
export const getStaffMemberAbsence = httpsCallable<
  { employeeId: string; from: string; to: string; absenceTypes?: string[] },
  StaffMemberAbsence
>(functions, 'getStaffMemberAbsence');

export interface AbsenceCalendarRow {
  employeeId: string;
  name: string;
  staffCode: string | null;
  teaching: boolean | null;
  cells: Record<string, string>;
}

export type DayKind = 'term' | 'holiday' | 'inset' | 'weekend' | 'unknown';

export interface AbsenceCalendar extends StaffAbsenceSyncState {
  from: string;
  to: string;
  clamped: boolean;
  staffTypeAvailable: boolean;
  absenceTypes: AbsenceTypeInfo[];
  /** On the calendar the tick-list decides what is shown; null = everything. */
  absenceTypeOverride: string[] | null;
  days: string[];
  /** yyyy-mm-dd → what kind of day it is, from the school's term dates.
   *  'unknown' when the school has not set them. */
  dayKinds: Record<string, DayKind>;
  rows: AbsenceCalendarRow[];
}

export const getStaffAbsenceCalendar = httpsCallable<
  { from: string; to: string; staffType?: StaffTypeFilter; absenceTypes?: string[] },
  AbsenceCalendar
>(functions, 'getStaffAbsenceCalendar');

/** Audited on the server — the moment staff health data leaves the platform. */
export const exportStaffAbsence = httpsCallable<
  { kind: 'bradford' | 'calendar' | 'person' | 'reviewPoints' | 'returns'; from?: string; to: string; staffType?: StaffTypeFilter; employeeId?: string; absenceTypes?: string[] },
  { csv: string; filename: string; rows: number }
>(functions, 'exportStaffAbsence');

// ─── Review points (KCSP-style procedure) ────────────────────────────────────

export interface ReviewPointResult {
  count: number;
  threshold: number;
  reached: boolean;
  /** The day the point was reached inside the window, or null. */
  reachedOn: string | null;
}

export interface LongTermResult {
  reached: boolean;
  reachedOn: string | null;
  startDate: string | null;
  lastDay: string | null;
  days: number;
  ongoing: boolean;
}

export interface PersonReviewPoints {
  window: { from: string; to: string };
  occasions: ReviewPointResult;
  days: ReviewPointResult;
  longTerm: LongTermResult;
  reached: boolean;
  firstReachedOn: string | null;
  lastAbsentDay: string | null;
}

export type StageState = 'waitingForReturn' | 'watching' | 'triggered' | 'lapsed' | 'none';

export interface StageEvaluation {
  window: { from: string; to: string } | null;
  measure: 'days' | 'occasions';
  threshold: number;
  count: number;
  met: boolean;
  metOn: string | null;
  state: StageState;
  consequence: string;
}

export interface CaseSummary {
  stage: CaseStage;
  track: CaseTrack;
  meetingDate: string | null;
  letterDate: string | null;
  monitorToOverride: string | null;
  certifiedUntil: string | null;
  evaluation: StageEvaluation;
}

export type CloseReason = 'completed' | 'leftTheSchool' | 'other';

/** A colleague's case in the absence procedure — dates and a stage, never a
 *  reason. Function-written; read only through the audited person view. */
export interface StaffAbsenceCase {
  employeeId: string;
  employeeName: string;
  status: 'open' | 'closed';
  track: CaseTrack;
  stage: CaseStage;
  meetingDate: string | null;
  letterDate: string | null;
  monitorFrom: string | null;
  monitorTo: string | null;
  /** An end date set in place of the computed one, where the procedure says
   *  review timeframes should be adjusted. A date, never a reason. */
  monitorToOverride: string | null;
  /** How far a fit note certifies the absence. A date, never the condition. */
  certifiedUntil: string | null;
  ohReferredOn: string | null;
  note: string;
  history: { stage: CaseStage; track: CaseTrack; meetingDate: string | null; letterDate: string | null; monitorFrom: string | null; recordedAt: string; recordedByName: string }[];
  openedAt: string;
  updatedAt: string;
  updatedByName: string;
  closedAt: string | null;
  closedReason: CloseReason | null;
}

export interface CaseInput {
  employeeId: string;
  stage: CaseStage;
  track: CaseTrack;
  meetingDate?: string | null;
  letterDate?: string | null;
  monitorFrom?: string | null;
  monitorToOverride?: string | null;
  certifiedUntil?: string | null;
  ohReferredOn?: string | null;
  note?: string;
}

/** Audited. Same stage as the open case = edit; a new stage must be the next one. */
export const saveStaffAbsenceCase = httpsCallable<CaseInput, { case: StaffAbsenceCase; evaluation: StageEvaluation }>(functions, 'saveStaffAbsenceCase');
/** Audited. */
export const closeStaffAbsenceCase = httpsCallable<{ employeeId: string; reason: CloseReason }, { case: StaffAbsenceCase }>(functions, 'closeStaffAbsenceCase');

export type NotNeededReason = 'leftTheSchool' | 'other';

export interface ReturnRecord {
  outcome: 'held' | 'notNeeded';
  heldOn: string | null;
  heldByName: string | null;
  notNeededReason: NotNeededReason | null;
  note: string;
  recordedByName: string;
  recordedAt: string;
}

/** A paper from the return-to-work meeting. Opened only through
 *  openStaffAbsenceDocument — there is never a URL. */
export interface StaffAbsenceDocument {
  id: string;
  employeeId: string;
  spellStart: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
  uploadedByUid: string;
  uploadedByName: string;
  uploadedAt: string;
}

export const attachReturnToWorkDocument = httpsCallable<
  { employeeId: string; spellStart: string; storagePath: string; fileName: string },
  { ok: boolean; document: StaffAbsenceDocument }
>(functions, 'attachReturnToWorkDocument');

/** Audited on every call; the bytes come back inline. */
export const openStaffAbsenceDocument = httpsCallable<
  { documentId: string },
  { name: string; contentType: string; dataBase64: string }
>(functions, 'openStaffAbsenceDocument');

/** The uploader only. Audited. */
export const removeStaffAbsenceDocument = httpsCallable<{ documentId: string }, { ok: boolean }>(functions, 'removeStaffAbsenceDocument');

/** One return from absence that needs (or had) a discussion. Dates are the
 *  server's, from the MIS record. */
export interface ReturnRow {
  employeeId: string;
  name: string;
  staffCode: string | null;
  teaching: boolean | null;
  spellStart: string;
  spellLastDay: string;
  absenceType: string;
  workingDays: number;
  returnDate: string;
  dueBy: string;
  overdue: boolean;
  record: ReturnRecord | null;
  documents: StaffAbsenceDocument[];
}

export interface ReturnToWorkList extends StaffAbsenceSyncState {
  from: string;
  asAt: string;
  today: string;
  clamped: boolean;
  windowStart: string;
  config: ReviewPointsConfig;
  staffTypeAvailable: boolean;
  absenceTypes: AbsenceTypeInfo[];
  absenceTypeOverride: string[] | null;
  maxWeeks: number;
  totals: { returns: number; due: number; overdue: number; held: number; notNeeded: number };
  rows: ReturnRow[];
}

export const getReturnToWorkList = httpsCallable<
  { from?: string; to?: string; staffType?: StaffTypeFilter; absenceTypes?: string[] },
  ReturnToWorkList
>(functions, 'getReturnToWorkList');

/** Audited. `clear` removes the record. */
export const recordReturnToWork = httpsCallable<
  { employeeId: string; spellStart: string; outcome: 'held' | 'notNeeded' | 'clear'; heldOn?: string | null; heldByName?: string | null; notNeededReason?: NotNeededReason; note?: string },
  { record: ReturnRecord | null }
>(functions, 'recordReturnToWork');

export interface ReviewPointRow {
  employeeId: string;
  name: string;
  staffCode: string | null;
  teaching: boolean | null;
  occasions: ReviewPointResult;
  days: ReviewPointResult;
  longTerm: LongTermResult;
  reached: boolean;
  firstReachedOn: string | null;
  newlyReached: boolean;
  lastAbsentDay: string | null;
  patterns: AbsencePattern[];
  /** The third review point (9.3(c)): a pattern that causes concern, when the
   *  school's procedure counts one. Undated — see firstReachedOn. */
  patternPoint: boolean;
  /** Where they are in the procedure, if a case is open. */
  case: CaseSummary | null;
}

export interface ReviewPointsReport extends StaffAbsenceSyncState {
  /** The date the report is as at — never in the future. */
  asAt: string;
  today: string;
  window: { from: string; to: string };
  windowStart: string;
  config: ReviewPointsConfig;
  staffTypeAvailable: boolean;
  includedAbsenceTypes: string[] | null;
  absenceTypes: AbsenceTypeInfo[];
  absenceTypeOverride: string[] | null;
  lookbackDays: number;
  termsConfigured: boolean;
  patternCounts: Record<string, number>;
  totals: {
    staff: number;
    headcount: number;
    reached: number;
    reachedOccasions: number;
    reachedDays: number;
    reachedLongTerm: number;
    newlyReached: number;
    withPattern: number;
    /** Those whose pattern is itself a review point. */
    reachedPattern: number;
    openCases: number;
    casesTriggered: number;
    /** Return-to-work discussions outstanding in the last eight weeks. */
    returnsOverdue: number;
    returnsDue: number;
  };
  rows: ReviewPointRow[];
}

export const getReviewPointsReport = httpsCallable<
  { to?: string; staffType?: StaffTypeFilter; absenceTypes?: string[] },
  ReviewPointsReport
>(functions, 'getReviewPointsReport');

export const syncStaffAbsencesNow = httpsCallable<Record<string, never>, {
  status: 'ok' | 'not-shared' | 'error'; count: number; removed: number;
// A five-year pull at a real school outruns the SDK's 70-second default. The
// server allows 540 s; waiting a little longer than that lets SyncBar tell a
// deadline apart from a failure.
}>(functions, 'syncStaffAbsencesNow', { timeout: 560_000 });
// ─── SEND — the annual review ─────────────────────────────────────────────────

export const openSendReview = httpsCallable<{ studentId: string; dueDate?: string | null }, { ok: boolean; reviewId: string; dueDate: string }>(functions, 'openSendReview');
export const requestSendContributions = httpsCallable<{ reviewId: string; uids: string[]; prompts?: string[] }, { ok: boolean; asked: number; alreadyAsked: number; emailed: number; dueDate: string }>(functions, 'requestSendContributions');
export const recordSendMeeting = httpsCallable<{ reviewId: string; meetingOn?: string | null; meetingLocation?: string; meetingNotes?: string; held: boolean }, { ok: boolean; stage: string }>(functions, 'recordSendMeeting');
export const sendSendRecommendation = httpsCallable<{ reviewId: string; recommendation: 'maintain' | 'amend' | 'cease' }, { ok: boolean; laResponseDueDate: string }>(functions, 'sendSendRecommendation');
export const recordLaResponse = httpsCallable<{ reviewId: string; respondedOn?: string | null; laOutcome?: string }, { ok: boolean }>(functions, 'recordLaResponse');
export const closeSendReview = httpsCallable<{ reviewId: string }, { ok: boolean; lastReviewDate: string; nextReviewDue: string | null }>(functions, 'closeSendReview');
export interface ReviewContributor { uid: string; name: string; status: 'open' | 'submitted'; dueDate: string; answers: string[] | null; submittedAt: unknown }
export const getSendReviewContributions = httpsCallable<{ reviewId: string }, { contributors: ReviewContributor[] }>(functions, 'getSendReviewContributions');

/** Active colleagues a SEND team member may name — uid and name only. */
export const listSendColleagues = httpsCallable<Record<string, never>, { people: { uid: string; name: string }[] }>(functions, 'listSendColleagues');

// ─── SEND — Section E outcomes, the sweep and oversight ───────────────────────

export const upsertSendOutcome = httpsCallable<{ outcomeId?: string; studentId: string; sectionERef?: string; outcomeText: string; baseline?: string; targetDate?: string | null; status?: string }, { ok: boolean; id: string }>(functions, 'upsertSendOutcome');
export const rateSendOutcome = httpsCallable<{ outcomeId: string; rating: 'notYet' | 'emerging' | 'onTrack' | 'met'; evidence: string; date?: string }, { ok: boolean; rating: string; status: string }>(functions, 'rateSendOutcome');
/** Super admin only — runs the daily sweep for one school on demand. */
export const runSendSweepNow = httpsCallable<{ schoolId: string }, { ok: boolean; registerUpdated: number; reviewsOpened: number; contributionsChased: number; laOverdueFlagged: number; passportsStale: number; snapshotWeek: string }>(functions, 'runSendSweepNow');
export interface SendOversightResult {
  today: string;
  latest: import('../types').SendComplianceSnapshot | null;
  history: { weekKey: string; computedOn: string; provisionPercent: number | null; overdue: number }[];
  lists: {
    reviewsOverdue: { id: string; studentId: string; studentName: string; yearGroup: string | null; nextReviewDue: string | null; reviewStage: string | null }[];
    laOverdue: { id: string; studentId: string; studentName: string; laResponseDueDate: string | null; recommendation: string | null }[];
    provisionsOverdue: { id: string; studentId: string; studentName: string; sectionFRef: string; description: string; deliveredByName: string | null; nextDueDate: string | null }[];
    passportsStale: { id: string; studentId: string; studentName: string }[];
  };
}
export const getSendOversight = httpsCallable<Record<string, never>, SendOversightResult>(functions, 'getSendOversight');
